import { ReactNode } from 'react'
import { childrenToString } from '~/common/childrenUtils'
import {
  asLink,
  Badge,
  BadgeProps,
  Box,
  CheckboxProps,
  Flex,
  FlexProps,
  forwardRef,
  Heading,
  HeadingProps,
  HStack,
  Link,
  LinkOverlay,
  LinkProps,
  StackProps,
  Text,
  TextProps,
  Tooltip,
  FavoriteCheckbox
} from '~/design_system'

export type TitleLockupSize = 'lg' | 'md'

/**
 * TitleLockup is a component that pairs a heading with other common supplementary components
 * - FavoriteCheckbox
 * - StatusBadge
 * - StatusSupportText
 * - Superscript
 *
 * @example
 * <TitleLockup>
 *   <TitleLockupSuperscript>
 *     Created {job.created_at} • ID {job.id}
 *   </TitleLockupSuperscript>
 *   <TitleLockupMain>
 *     <TitleLockupHeading href={JOB_DETAILS_HREF}>{job.title}</TitleLockupHeading>
 *     <TitleLockupFavoriteCheckbox {...favoriteCheckboxProps} />
 *   </TitleLockupMain>
 *   <TitleLockupBottom>
 *     <TitleLockupStatusBadge {...statusBadgeProps} />
 *     <TitleLockupSupportText {...supportTextProps} />
 *     <TitleLockupIcon {...titleLockupIconProps} />
 *   </TitleLockupBottom>
 * </TitleLockup>
 */
export function TitleLockup({ size = 'lg', ...props }: StackProps & { size?: TitleLockupSize }) {
  return <Flex data-title-lockup data-title-lockup-size={size} direction="column" {...props} />
}

/**
 * The small gray text that can optionally appear above a title
 */
export function TitleLockupSuperscript(props: TextProps) {
  return <Text data-title-lockup-superscript lineHeight="shorter" color="gray.500" fontSize="xs" mb={2} {...props} />
}

/**
 * The small gray text that can optionally appear below a title
 */
export function TitleLockupSubscript(props: TextProps) {
  return <Text data-title-lockup-subscript textStyle="subtextSmall" mt={1} {...props} />
}

/**
 * The main container within a TitleLockup, holds the Title and FavoriteCheckbox
 */
export const TitleLockupMain = (props: StackProps) => <HStack data-title-lockup-main {...props} />

/**
 * The first component within TitleLockupMain. If an `href` is passed it will become a link.
 */
export function TitleLockupHeading({
  children,
  href,
  rightElement,
  isTruncated,
  ...props
}: HeadingProps & { href?: string; rightElement?: ReactNode | undefined; isTruncated?: boolean }) {
  const hoveredStyles = {
    color: 'var(--vndly-color-link)',
    textDecoration: 'underline',
    _hover: {
      color: 'var(--vndly-color-link-hover)',
      cursor: 'pointer'
    }
  }
  let heading = (
    <Heading
      data-title-lockup-heading
      fontSize="var(--vndly-font-size-xl)"
      my={0}
      as="h2"
      size="sm"
      noOfLines={3}
      wordBreak={isTruncated ? 'break-all' : 'normal'}
      sx={{
        '[data-title-lockup-size="lg"] &': { fontSize: 'var(--vndly-font-size-xl)', lineHeight: 7 },
        '[data-title-lockup-size="md"] &': { fontSize: 'var(--vndly-font-size-md)', lineHeight: 6 }
      }}
      title={childrenToString(children)}
      {...(props.onClick != null && hoveredStyles)}
      {...props}>
      {children}
    </Heading>
  )

  if (href != null) {
    heading = (
      <LinkOverlay {...asLink(href)} data-title-lockup-heading-link minW={0} {...hoveredStyles}>
        {heading}
      </LinkOverlay>
    )
  }

  return (
    <>
      {heading}
      {rightElement}
    </>
  )
}

/**
 * A way to favorite an item. Placed to the right of the Title within TitleLockupMain.
 */
export function TitleLockupFavoriteCheckbox(props: CheckboxProps) {
  return <FavoriteCheckbox data-title-lockup-favorite-checkbox data-hoist-above-parent-hover {...props} />
}

/**
 * The bottom container within a TitleLockup, holds the StatusBadge and StatusSupportText
 */
export function TitleLockupBottom(props: FlexProps) {
  return (
    <Flex
      data-title-lockup-bottom
      align="center"
      flexWrap={{ base: 'wrap', sm: 'nowrap' }}
      sx={{
        gap: '8px',
        '[data-title-lockup-size="lg"] &': { lineHeight: 6 },
        '[data-title-lockup-size="md"] &': { lineHeight: '18px' }
      }}
      {...props}
    />
  )
}

/**
 * Shows the status of an item. Placed below the title within TitleLockupBottom.
 */
export function TitleLockupStatusBadge(props: BadgeProps) {
  return (
    <Badge
      size="sm"
      data-title-lockup-status-badge
      lineHeight={1.5}
      sx={{
        '[data-title-lockup-size="lg"] &': { my: 1 },
        '[data-title-lockup-size="md"] &': { my: 0 }
      }}
      {...props}
    />
  )
}

export function TitleLockupIcon(props: { tooltip: string; icon: ReactNode }) {
  const { tooltip, icon } = props
  return (
    <Box data-testid="lockup-icon" data-hoist-above-parent-hover display="flex" alignSelf="end" paddingY={1.5}>
      <Tooltip label={tooltip} shouldWrapChildren placement="top">
        {icon}
      </Tooltip>
    </Box>
  )
}

export function TitleLockupAdditionalInfo(props: StackProps) {
  /**
   * TitleLockupAdditionalInfo
   * Provides an additional third row to the title section for things like ratings, text, or other statuses.
   * The second move around slightly depending on if in card view and the component size
   */
  return <Box data-title-additional-info flexDir="column" {...props} />
}

type TitleLockupStatusSupportTextProps = (LinkProps | TextProps) & { href?: string }
/**
 * Supportive text placed to the right of a status badge. Helps to inform the user about additional relevant info
 * pertaining to or supporting the current status. Optionally pass an `href` to convert to a link.
 */
export const TitleLockupSupportText = forwardRef<TitleLockupStatusSupportTextProps, 'li'>(
  ({ href, children, ...props }, ref) => {
    const sharedWrapStyles = {
      flex: 1,
      minW: { base: 'auto', md: 0 },
      maxW: 'full'
    }
    const sharedTextProps = {
      textStyle: 'bodySmallSemi',
      isTruncated: true,
      title: childrenToString(children),
      ...props
    }
    let item = null
    if (href != null) {
      item = (
        // @ts-ignore
        <Link
          data-title-lockup-status-support-text
          position="relative"
          href={href}
          variant="link"
          {...(sharedTextProps as LinkProps)}>
          {children}
        </Link>
      )
    } else {
      item = (
        <Text data-title-lockup-status-support-text {...(sharedTextProps as TextProps)}>
          {children}
        </Text>
      )
    }
    return (
      <Box ref={ref} {...sharedWrapStyles}>
        {item}
      </Box>
    )
  }
)
